import { useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";
import {
  Container,
  Center,
  Loader,
  Stack,
  Group,
  Title,
  Text,
  Button,
  ActionIcon,
  TextInput,
  Modal,
  Grid,
  Badge,
  Switch,
  CopyButton,
  Tooltip,
  Alert,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconCheck,
  IconCopy,
  IconPencil,
  IconTrash,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  fetchBundle,
  updateBundle,
  deleteBundle,
  deleteGalleryImage,
  getImage,
} from "../api";
import { GalleryImageCard } from "./GalleryImageCard";
import { GalleryVideoCard } from "./GalleryVideoCard";
import { useStore } from "../store";

export default function BundleDetailPage() {
  const [, params] = useRoute("/bundle/:id");
  const [, setLocation] = useLocation();
  const setImageData = useStore((state) => state.setImageData);

  const [bundle, setBundle] = useState(null);
  const [chainSource, setChainSource] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [shareBusy, setShareBusy] = useState(false);

  const bundleId = params?.id;

  useEffect(() => {
    if (!bundleId) return;
    setIsLoading(true);
    setChainSource(null);
    fetchBundle(bundleId)
      .then(async (data) => {
        setBundle(data);
        setEditTitle(data.title);
        // For VideoChain bundles, fetch the origin video separately — it stays
        // in its original group, so it's not in `data.images`.
        if (data.kind === "VideoChain" && data.chainSourceLogicalImageId) {
          try {
            const src = await getImage(data.chainSourceLogicalImageId);
            setChainSource(src);
          } catch {
            /* origin may have been deleted; render chain without source */
          }
        }
      })
      .catch((err) => {
        notifications.show({
          title: "Couldn't load bundle",
          message: String(err.message ?? err),
          color: "red",
        });
        setLocation("/gallery");
      })
      .finally(() => setIsLoading(false));
  }, [bundleId, setLocation]);

  const onRename = async () => {
    if (!editTitle.trim()) return;
    try {
      const updated = await updateBundle(bundleId, {
        title: editTitle.trim(),
        description: bundle.description,
        coverImageId: bundle.coverImageId,
        isPublic: bundle.isPublic,
      });
      setBundle({ ...bundle, ...updated });
      setEditOpen(false);
      notifications.show({ title: "Bundle renamed", color: "green" });
    } catch (err) {
      notifications.show({
        title: "Rename failed",
        message: String(err.message ?? err),
        color: "red",
      });
    }
  };

  const onDeleteBundle = async () => {
    if (
      !window.confirm(
        "Delete this bundle? The photos inside will return to your gallery."
      )
    )
      return;
    try {
      await deleteBundle(bundleId);
      notifications.show({ title: "Bundle deleted", color: "green" });
      setLocation("/gallery");
    } catch (err) {
      notifications.show({
        title: "Delete failed",
        message: String(err.message ?? err),
        color: "red",
      });
    }
  };

  const onToggleShare = async (next) => {
    setShareBusy(true);
    try {
      const updated = await updateBundle(bundleId, {
        title: bundle.title,
        description: bundle.description,
        coverImageId: bundle.coverImageId,
        isPublic: next,
      });
      setBundle((b) => ({ ...b, ...updated }));
      notifications.show({
        title: next ? "Bundle shared" : "Bundle unshared",
        message: next ? "Anyone with the link can now view it." : undefined,
        color: "green",
      });
    } catch (err) {
      notifications.show({
        title: "Couldn't update sharing",
        message: String(err.message ?? err),
        color: "red",
      });
    } finally {
      setShareBusy(false);
    }
  };

  const onDeleteImage = async (imageId) => {
    try {
      await deleteGalleryImage(imageId);
      setBundle((b) => ({
        ...b,
        images: b.images.filter((img) => img.logicalImageId !== imageId),
        childCount: b.childCount - 1,
      }));
    } catch (err) {
      notifications.show({
        title: "Couldn't delete image",
        message: String(err.message ?? err),
        color: "red",
      });
    }
  };

  if (isLoading) {
    return (
      <Center mt="xl">
        <Loader size="xl" />
      </Center>
    );
  }
  if (!bundle) return null;

  const imageSpan = { base: 6, xs: 6, sm: 4, md: 3, lg: 3, xl: 3 };
  const videoSpan = { base: 6, xs: 6, sm: 6, md: 6, lg: 4, xl: 4 };

  const isVideoChain = bundle.kind === "VideoChain";
  // For chain bundles, walk parentLogicalImageId forward from the source so
  // the playback order is V0 -> V1 -> V2... A child whose parent isn't in the
  // bundle (or no parent at all) sorts to the end as a fallback so we never
  // drop images.
  const orderedChildren = (() => {
    if (!isVideoChain) return bundle.images;
    const byParent = new Map();
    bundle.images.forEach((img) => {
      const parent = img.parentLogicalImageId ?? null;
      if (!byParent.has(parent)) byParent.set(parent, []);
      byParent.get(parent).push(img);
    });
    const ordered = [];
    const visited = new Set();
    const walk = (parentId) => {
      const children = byParent.get(parentId) ?? [];
      children.forEach((c) => {
        if (visited.has(c.logicalImageId)) return;
        visited.add(c.logicalImageId);
        ordered.push(c);
        walk(c.logicalImageId);
      });
    };
    // Start from the chain origin if we have it, otherwise from null parent.
    walk(bundle.chainSourceLogicalImageId ?? null);
    walk(null);
    // Anything still un-walked (broken parent reference): append in original order.
    bundle.images.forEach((img) => {
      if (!visited.has(img.logicalImageId)) ordered.push(img);
    });
    return ordered;
  })();

  return (
    <Container fluid p="md">
      <Stack>
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              onClick={() => setLocation("/gallery")}
              aria-label="Back to gallery"
            >
              <IconArrowLeft size={20} />
            </ActionIcon>
            <Title order={3}>{bundle.title}</Title>
            <Badge variant="light">
              {bundle.childCount}{" "}
              {bundle.childCount === 1 ? "photo" : "photos"}
            </Badge>
          </Group>
          <Group gap="xs">
            <Button
              size="xs"
              variant="default"
              leftSection={<IconPencil size={14} />}
              onClick={() => setEditOpen(true)}
            >
              Rename
            </Button>
            <Button
              size="xs"
              variant="default"
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={onDeleteBundle}
            >
              Delete bundle
            </Button>
          </Group>
        </Group>

        <Group gap="sm" wrap="wrap">
          <Switch
            checked={!!bundle.isPublic}
            disabled={shareBusy}
            onChange={(e) => onToggleShare(e.currentTarget.checked)}
            label="Share publicly"
            description={
              bundle.isPublic
                ? "Anyone with the link can view this bundle."
                : "Off — only you can see this bundle."
            }
          />
          {bundle.isPublic && (
            <CopyButton value={`${window.location.origin}/b/${bundle.id}`}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? "Copied" : "Copy public link"}>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={
                      copied ? <IconCheck size={14} /> : <IconCopy size={14} />
                    }
                    onClick={copy}
                  >
                    {copied ? "Copied" : "Copy link"}
                  </Button>
                </Tooltip>
              )}
            </CopyButton>
          )}
        </Group>

        {bundle.description && (
          <Text c="dimmed" size="sm">
            {bundle.description}
          </Text>
        )}

        {isVideoChain && (
          <Alert
            color="grape"
            variant="light"
            p="xs"
            title="Video chain"
          >
            Each clip starts from the last frame of the previous one. Expect
            color drift and motion slowdown after the third extension.
          </Alert>
        )}

        {bundle.images.length === 0 && !chainSource ? (
          <Text c="dimmed" ta="center" mt="xl">
            This bundle is empty.
          </Text>
        ) : (
          <Grid gutter={10}>
            {isVideoChain && chainSource && (
              <Grid.Col
                key={`chain-source-${chainSource.logicalImageId}`}
                span={videoSpan}
              >
                <div style={{ position: "relative" }}>
                  <Badge
                    color="grape"
                    variant="filled"
                    size="sm"
                    style={{
                      position: "absolute",
                      top: 6,
                      left: 6,
                      zIndex: 2,
                    }}
                  >
                    Source
                  </Badge>
                  {chainSource.videoUrl ? (
                    <GalleryVideoCard
                      image={chainSource}
                      onClick={() => {
                        setImageData(chainSource);
                        setLocation("/video");
                      }}
                    />
                  ) : (
                    <GalleryImageCard
                      image={chainSource}
                      onClick={() => {
                        setImageData(chainSource);
                        setLocation("/editor");
                      }}
                    />
                  )}
                </div>
              </Grid.Col>
            )}
            {orderedChildren.map((image) =>
              image.videoUrl ? (
                <Grid.Col key={image.logicalImageId} span={videoSpan}>
                  <GalleryVideoCard
                    image={image}
                    onDelete={onDeleteImage}
                    onClick={() => {
                      setImageData(image);
                      setLocation("/video");
                    }}
                  />
                </Grid.Col>
              ) : (
                <Grid.Col key={image.logicalImageId} span={imageSpan}>
                  <GalleryImageCard
                    image={image}
                    onDelete={onDeleteImage}
                    onClick={() => {
                      setImageData(image);
                      setLocation("/editor");
                    }}
                  />
                </Grid.Col>
              )
            )}
          </Grid>
        )}
      </Stack>

      <Modal
        opened={editOpen}
        onClose={() => setEditOpen(false)}
        title="Rename bundle"
      >
        <Stack>
          <TextInput
            value={editTitle}
            onChange={(e) => setEditTitle(e.currentTarget.value)}
            label="Title"
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onRename} disabled={!editTitle.trim()}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
